export type DscsaRiskLevel = "on_track" | "needs_attention" | "priority_gaps";

export interface DscsaAssessment {
  score: number;
  level: DscsaRiskLevel;
  label: string;
  summary: string;
}

const RISK_LEVELS: Record<DscsaRiskLevel, Omit<DscsaAssessment, "score" | "level">> = {
  on_track: {
    label: "On track",
    summary: "Your answers suggest your current DSCSA process is generally on track. Keep documenting and reviewing it as requirements evolve.",
  },
  needs_attention: {
    label: "Needs attention",
    summary: "Your answers suggest one or more DSCSA processes need attention. A documented review can help you decide what to tighten first.",
  },
  priority_gaps: {
    label: "Priority gaps",
    summary: "Your answers suggest several DSCSA processes need attention soon. Start with transaction records, receiving checks, and written procedures.",
  },
};

function answerScore(answer: unknown, notSureScore: number): number {
  if (typeof answer !== "string") return 2;
  const normalized = answer.trim().toLowerCase();
  if (normalized === "yes") return 0;
  if (normalized === "not sure") return notSureScore;
  return 2;
}

/**
 * Produces a directional self-assessment, not a regulatory determination.
 * A "yes" answer is the lowest-risk response for each question; unanswered or
 * unrecognized values are scored conservatively as a gap.
 */
export function calculateDscsaAssessment(answers: Record<string, unknown> = {}): DscsaAssessment {
  const score =
    answerScore(answers.q1, 1) +
    answerScore(answers.q2, 0) +
    answerScore(answers.q3, 1);
  const level: DscsaRiskLevel =
    score <= 1 ? "on_track" : score <= 3 ? "needs_attention" : "priority_gaps";

  return { score, level, ...RISK_LEVELS[level] };
}

/** Keeps older queued records readable while normalizing the former label. */
export function assessmentFromRiskLevel(level: unknown): DscsaAssessment {
  if (level === "on_track" || level === "needs_attention" || level === "priority_gaps") {
    return { score: 0, level, ...RISK_LEVELS[level] };
  }
  return { score: 0, level: "needs_attention", ...RISK_LEVELS.needs_attention };
}